import React from 'react';
import { Link } from 'react-router-dom';
import { Dot,Users, Quote, Star } from 'lucide-react';
import { motion } from 'framer-motion';

const TestimonialCard: React.FC<{
  quote: string;
  author: string;
  role: string;
  org: string;
}> = ({ quote, author, role, org }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="glass-card rounded-[2.5rem] p-6 border border-[var(--card-border)] transition-theme"
  >
    <div className="flex items-start gap-4">
      <Quote className="w-16 h-16 text-[var(--accent-blue)] mt-1" />
      <p className="text-sm text-[var(--text-muted)] italic">{quote}</p>
    </div>
    <div className="flex items-center gap-4 mt-6">
      <div className="w-12 h-12 bg-[var(--accent-blue-soft)] rounded-full flex items-center justify-center text-[var(--accent-blue)]">
        <Users size={20} />
      </div>
      <div>
        <h4 className="font-bold text-[var(--text-main)] text-sm">{author}</h4>
        <p className="text-[var(--text-muted)] text-[10px] uppercase tracking-wider font-bold">{role}, {org}</p>
      </div>
    </div>
  </motion.div>
);

const IndustryProjects: React.FC = () => {
  return (
    <div className="container mx-auto px-6 max-w-6xl space-y-32 pt-32">
      <div className="text-center">
        <h1 className="text-5xl font-bold mb-6 text-[var(--text-main)] transition-theme">Industry Projects</h1>
        <p className="text-[var(--text-muted)] max-w-2xl mx-auto transition-theme">
          Custom optical and quantum engineering collaborations bridging the gap between laboratories and deployable hardware.
        </p>
      </div>

      <section id="industry">
        <div className="bg-white rounded-[3rem] p-12 text-[var(--text-main)] shadow-2xl transition-theme">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
             <div className="space-y-6">
               <h3 className="text-2xl font-bold">Collaborations & Consulting</h3>
               <p className="text-[var(--text-muted)]">
                 We collaborate with industry partners on custom projects, spanning design, simulation, and 
                 experimental validation across various photonic and quantum applications.
               </p>
               <ul className="grid grid-cols-1 gap-4">
                 {[
                   "Precision optical system design",
                   "Interferometer development",
                   "Quantum optics experimental setups",
                   "Feasibility studies",
                   "Expert Consultation"
                 ].map((item, i) => (
                   <li key={i} className="flex items-center gap-3 text-[var(--text-muted)] text-sm">
                     <Dot className="w-5 h-5 text-[var(--accent-blue)]" />
                     {item}
                   </li>
                 ))}
               </ul>
               <Link to="/contact" className="inline-block px-8 py-4 bg-[var(--accent-blue)] text-white rounded-2xl font-bold hover:brightness-110 transition-all mt-6 shadow-lg shadow-blue-500/20">
                 Explore Collaborations
               </Link>
             </div>
             <div className="flex flex-col gap-6 w-full">
                <div className="w-full aspect-[16/9] bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden group relative">
                  <img 
                    src="/images/softwareindustry_preciqube.jpg" 
                    alt="Software Industry Project" 
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500 relative z-10"
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)] text-xs italic text-center p-2 z-0">
                     
                  </div>
                </div>
                <div className="w-full aspect-[16/9] bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden group relative">
                  <img 
                    src="/images/Screenshot from 2025-12-28 14-08-14.png" 
                    alt="Hardware Industry Project" 
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500 relative z-10"
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)] text-xs italic text-center p-2 z-0">
                      
                  </div>
                </div>
             </div>
          </div>
        </div>
      </section>

       {/* Testimonials Section */}
      {/* <section id="testimonials" className="space-y-16">
        <div className="text-center space-y-4">
          
          <h2 className="text-3xl font-bold text-[var(--text-main)] transition-theme">Partner Testimonials</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <TestimonialCard 
            quote="The precision with which the PreciQube team handled our interferometric design project was outstanding. Their deep understanding of quantum noise limits provided us with insights we hadn't considered."
            author="Dr. Suresh V."
            role="Research Lead"
            org="Photonics Research Group"
          />
          <TestimonialCard 
            quote="PreciQube's OptiCore tool has significantly reduced our simulation iteration time. Their consulting on laser beam stability helped us achieve a 40% improvement in measurement repeatability."
            author="Ananya R."
            role="Senior Systems Engineer"
            org="QuantumTech Labs"
          />
          <TestimonialCard 
            quote="Collaborating with Prakhar and his team was seamless. They bridge the gap between abstract physics and deployable engineering better than any startup we've worked with in the deep-tech space."
            author="Kartik Mehra"
            role="Founder"
            org="OptiDesign India"
          />
          <TestimonialCard 
            quote="Their approach to intellectual property and rigorous design validation gave our clinical partners the confidence to proceed with experimental trials. Truly a technical powerhouse."
            author="Sarah Thompson"
            role="Head of Innovations"
            org="Global Healthcare Partners"
          />
        </div>
      </section> */}

      {/* <div id="future" className="bg-[var(--accent-blue-soft)]/20 rounded-[3rem] p-12 text-center border border-[var(--accent-blue)]/20 backdrop-blur transition-theme">
        <h3 className="text-2xl font-bold text-[var(--text-main)] mb-4 transition-theme">Looking Ahead</h3>
        <p className="text-[var(--text-muted)] max-w-2xl mx-auto mb-8 transition-theme text-sm">
          These collaborations form the foundation for PreciQube's 
          long-term vision in quantum-enhanced diagnostic systems.
        </p>
        <div className="flex justify-center gap-12 flex-wrap">
          <div className="text-center">
            <p className="text-[var(--accent-blue)] font-bold mb-1 transition-theme">Precision</p>
            <p className="text-[var(--text-muted)] text-[9px] uppercase tracking-widest font-bold transition-theme">and reliability</p>
          </div>
          <div className="text-center">
            <p className="text-[var(--accent-blue)] font-bold mb-1 transition-theme">Scalability</p>
            <p className="text-[var(--text-muted)] text-[9px] uppercase tracking-widest font-bold transition-theme">and reproducibility</p>
          </div>
          <div className="text-center">
            <p className="text-[var(--accent-blue)] font-bold mb-1 transition-theme">Strong IP</p>
            <p className="text-[var(--text-muted)] text-[9px] uppercase tracking-widest font-bold transition-theme">Foundations</p>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default IndustryProjects;
